import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import axiosInstance from "../config/axios";
import { useRole } from "../context/RoleContext";
import WorkflowBadge from "./WorkflowBadge";
import {
  buildAgentTradeStatusRow,
  enrichTradesForAgentTradeStatus,
  formatStatusMoney,
} from "../utils/agentTradeStatusUtils";

function StatusValue({ ok, label }) {
  return (
    <p className="font-semibold text-gray-900">
      <span className={ok ? "text-emerald-700" : "text-gray-800"}>
        {ok ? "Yes" : "Not yet"}
      </span>
      {label ? <span className="font-normal text-gray-600"> · {label}</span> : ""}
    </p>
  );
}

const AgentTradeStatus = () => {
  const { appUser, isAgent } = useRole();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [tradesRes, eftRes, reEftRes] = await Promise.all([
          axiosInstance.get("/trades"),
          axiosInstance.get("/commission-trust-eft").catch(() => ({ data: [] })),
          axiosInstance
            .get("/real-estate-trust-eft")
            .catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        const efts = eftRes.data || [];
        const reEfts = reEftRes.data || [];
        const trades = enrichTradesForAgentTradeStatus(
          tradesRes.data || [],
          efts,
          reEfts,
        );
        setRows(
          trades.map((trade) =>
            buildAgentTradeStatusRow(trade, efts, isAgent ? appUser : null),
          ),
        );
      } catch (_) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [appUser, isAgent]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-blue-900 mb-2">Trade Status</h1>
        <p className="text-gray-600 mb-6">
          Commission received and commission paid out
          {isAgent ? " for your trades" : ""}
          {appUser?.displayName ? ` (${appUser.displayName})` : ""}.
        </p>
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-900"></div>
          </div>
        ) : rows.length === 0 ? (
          <p className="text-gray-500">No trades found for your account yet.</p>
        ) : (
          <div className="space-y-4">
            {rows.map((row) => (
              <div
                key={row.id}
                className="bg-white rounded-lg shadow p-5 border border-gray-100"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Trade #{row.tradeNumber}
                    </h2>
                    <p className="text-gray-600">{row.address || "No address"}</p>
                    {row.isPending ? (
                      <p className="text-sm text-amber-800 mt-1">
                        Pending until compliance is complete
                      </p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                        row.isPending
                          ? "bg-yellow-100 text-yellow-800"
                          : row.dealStatus === "Closed" ||
                              row.dealStatus === "Completed"
                            ? "bg-green-100 text-green-800"
                            : row.dealStatus === "Fallen Thru"
                              ? "bg-red-100 text-red-800"
                              : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {row.dealStatus}
                    </span>
                    <WorkflowBadge record={row.record} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  <div className="rounded-md bg-gray-50 p-3">
                    <p className="text-sm text-gray-500">Commission received</p>
                    <StatusValue ok={row.received} label={row.receivedDate} />
                    {row.received && row.receivedAmount > 0 ? (
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {formatStatusMoney(row.receivedAmount)}
                      </p>
                    ) : null}
                    {!row.received && row.agentDue > 0 ? (
                      <p className="text-sm text-gray-600 mt-1">
                        Expected: {formatStatusMoney(row.agentDue)}
                      </p>
                    ) : null}
                    {row.received &&
                    row.agentDue > 0 &&
                    Math.abs(row.agentDue - row.receivedAmount) > 0.01 ? (
                      <p className="text-sm text-gray-600">
                        Your commission: {formatStatusMoney(row.agentDue)}
                      </p>
                    ) : null}
                    {row.trustTotal > 0 ? (
                      <p className="text-sm text-gray-600">
                        Trust deposit: {formatStatusMoney(row.trustTotal)}
                        {row.trustDepositDate
                          ? ` · ${row.trustDepositDate}`
                          : ""}
                      </p>
                    ) : null}
                  </div>
                  <div className="rounded-md bg-gray-50 p-3">
                    <p className="text-sm text-gray-500">Commission paid out</p>
                    <StatusValue
                      ok={row.paidOut}
                      label={row.payouts.length ? "" : row.paidDate}
                    />
                    {row.payouts.length > 1 && row.paidAmount > 0 ? (
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        Total: {formatStatusMoney(row.paidAmount)}
                      </p>
                    ) : null}
                    {row.payouts.length > 0 ? (
                      <div className="mt-2 space-y-2">
                        {row.payouts.map((payout) => (
                          <div
                            key={payout.id}
                            className="text-sm text-gray-800 border-t border-gray-200 pt-2 first:border-0 first:pt-0"
                          >
                            <p className="font-medium text-gray-900">
                              {payout.eftNumber
                                ? `EFT #${payout.eftNumber}`
                                : "EFT issued"}
                            </p>
                            {payout.date ? (
                              <p className="text-gray-600">
                                Paid: {payout.date}
                              </p>
                            ) : null}
                            {payout.issuedDate ? (
                              <p className="text-gray-600">
                                EFT issued: {payout.issuedDate}
                              </p>
                            ) : null}
                            {payout.amount > 0 ? (
                              <p className="text-gray-600">
                                Amount: {formatStatusMoney(payout.amount)}
                              </p>
                            ) : null}
                            {!isAgent && payout.agentName ? (
                              <p className="text-gray-600">{payout.agentName}</p>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : null}
                    {!row.paidOut && row.agentDue > 0 ? (
                      <p className="text-sm text-gray-600 mt-1">
                        Due: {formatStatusMoney(row.agentDue)}
                      </p>
                    ) : null}
                  </div>
                  <div className="rounded-md bg-gray-50 p-3">
                    <p className="text-sm text-gray-500">Invoice issued</p>
                    {row.invoiceDate ? (
                      <p className="font-semibold text-gray-900">
                        {row.invoiceDate}
                      </p>
                    ) : (
                      <p className="font-semibold text-gray-800">Not yet</p>
                    )}
                  </div>
                  <div className="rounded-md bg-gray-50 p-3">
                    <p className="text-sm text-gray-500">Conditions</p>
                    {row.conditions.length === 0 ? (
                      <p className="font-semibold text-gray-900">None listed</p>
                    ) : (
                      <ul className="text-sm text-gray-800 space-y-1">
                        {row.conditions.map((c, idx) => (
                          <li key={idx}>{c.display || c.conditionText}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentTradeStatus;
